<script setup lang="ts">
import { tailwindSurfaceFontClass } from '@@/bits/font'
import { __ } from '@@/bits/intl'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfaceQrCodeStore } from '@@/pinia/surface_qr_code'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import SurfaceBuilder from '@@/vuecomponents/SurfaceBuilder.vue'
import SurfaceQrCode from '@@/vuecomponents/SurfaceQrCode.vue'
import SurfaceTitleDescription from '@@/vuecomponents/SurfaceTitleDescription.vue'
import ProcessedImage from '@@/vuecomponents/processed_image.vue'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

const surfaceStore = useSurfaceStore()
const { title, description, portrait, fontId, isEmbedded, colorScheme, links } = storeToRefs(surfaceStore)

const surfacePermissionsStore = useSurfacePermissionsStore()
const { canIAdminister } = storeToRefs(surfacePermissionsStore)

const surfaceSettingsStore = useSurfaceSettingsStore()
const { showSettingsPanel } = surfaceSettingsStore

const surfaceQrCodeStore = useSurfaceQrCodeStore()
const { pinnedQrCodeUrl, xPinnedQrCode } = storeToRefs(surfaceQrCodeStore)

const fontClass = computed<string>(() => tailwindSurfaceFontClass(fontId.value))

const tryModify = (): void => {
  if (canIAdminister.value && !isEmbedded.value) {
    showSettingsPanel()
  }
}
</script>

<template>
  <div data-testid="surfaceTitleMap">
    <div v-if="xPinnedQrCode" class="h-8 mb-1.5 pb-1">
      <SurfaceQrCode :src="pinnedQrCodeUrl" :width="32" :height="32" />
    </div>
    <div v-else-if="portrait" class="h-8 mb-1.5 pb-1">
      <ProcessedImage
        :src="portrait"
        :width="32"
        :height="32"
        :transforms="{ preset: 'icon', width: 32, height: 32 }"
      />
    </div>
    <div class="h-3.75 mb-1">
      <SurfaceBuilder />
    </div>
    <h1
      :class="{
        'mb-[4px]': true,
        [fontClass]: true,
        'text-17-21 font-semibold break-words whitespace-break-spaces': true,
        'text-dark-text-100': colorScheme === 'light',
        'text-light-text-100': colorScheme === 'dark',
      }"
      @dblclick="tryModify"
      v-text="title || __('Untitled padlet')"
    />
    <SurfaceTitleDescription
      v-if="description"
      :text="description"
      :font-id="fontId"
      :color-scheme="colorScheme"
      @dblclick.native="tryModify"
    />
  </div>
</template>
